import { Button, Card, Input, Tabs, Typography } from "@arco-design/web-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
  bindWechatIdentity,
  type AuthSession,
  loginAccount,
  loginWithWechatCode,
  registerAccount,
  resetAuthSessionQueries,
  resolveSafeAuthReturnPath,
} from "..";
import { writeHttpAccessToken } from "../../../lib/http/accessToken";
import { isHttpClientError } from "../../../lib/http/errors";

const { Text, Title } = Typography;

type AuthMode = "login" | "register";

type LoginPageProps = {
  initialMode?: AuthMode;
};

/** 登录与注册页面。 */
export function LoginPage({ initialMode = "login" }: LoginPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const wechatCodeHandledRef = useRef(false);
  const [mode, setMode] = useState<AuthMode>(searchParams.get("mode") === "register" ? "register" : initialMode);
  const [loginValue, setLoginValue] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [registerUsername, setRegisterUsername] = useState("");
  const [registerRealname, setRegisterRealname] = useState("");
  const [registerPhone, setRegisterPhone] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [wechatBinding, setWechatBinding] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const wechatCode = searchParams.get("wechat_code") ?? searchParams.get("code");
  const loginReason = searchParams.get("reason");

  useEffect(() => {
    if (loginReason === "expired") {
      setErrorMessage("登录状态已失效，请重新登录。");
    }
  }, [loginReason]);

  useEffect(() => {
    if (!wechatCode || wechatCodeHandledRef.current) {
      return;
    }
    wechatCodeHandledRef.current = true;
    void loginWithWechatCode(wechatCode)
      .then(handleAuthSession)
      .catch(() => {
        setErrorMessage("微信未绑定账号，请先使用账号登录或注册。");
      });
  }, [wechatCode]);

  const handleLoginSubmit = async () => {
    setSubmitting(true);
    setErrorMessage("");
    try {
      const session = await loginAccount({
        login: loginValue.trim(),
        password: loginPassword,
      });
      handleAuthSession(session);
    } catch (error) {
      setErrorMessage(resolveLoginErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegisterSubmit = async () => {
    setSubmitting(true);
    setErrorMessage("");
    try {
      const session = await registerAccount({
        username: registerUsername.trim(),
        password: registerPassword,
        realname: registerRealname.trim(),
        phone: registerPhone.trim() || null,
        email: registerEmail.trim() || null,
      });
      writeHttpAccessToken(session.accessToken);
      resetAuthSessionQueries(queryClient);
      if (wechatCode) {
        setWechatBinding(true);
        try {
          await bindWechatIdentity(wechatCode);
        } finally {
          setWechatBinding(false);
        }
      }
      navigateAfterAuthentication();
    } catch {
      setErrorMessage("注册失败，请检查账号信息。");
    } finally {
      setSubmitting(false);
    }
  };

  const handleWechatLoginClick = async () => {
    if (!wechatCode) {
      return;
    }
    setErrorMessage("");
    setSubmitting(true);
    try {
      const session = await loginWithWechatCode(wechatCode);
      handleAuthSession(session);
    } catch {
      setErrorMessage("微信登录失败，请先确认账号已绑定微信。");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAuthSession = (session: AuthSession) => {
    writeHttpAccessToken(session.accessToken);
    resetAuthSessionQueries(queryClient);
    navigateAfterAuthentication();
  };

  const navigateAfterAuthentication = () => {
    navigate(resolveSafeAuthReturnPath(location.state), { replace: true });
  };

  return (
    <main
      style={{
        display: "grid",
        minHeight: "100vh",
        placeItems: "center",
        background: "#f6f8fb",
        padding: 24,
      }}
    >
      <Card
        style={{
          width: "min(440px, 100%)",
          borderRadius: 8,
          boxShadow: "0 18px 46px rgba(15, 23, 42, 0.12)",
        }}
      >
        <Title heading={3} style={{ marginTop: 0, marginBottom: 4, letterSpacing: 0 }}>
          FlowerMaster
        </Title>
        <Text type="secondary">用户认证</Text>

        <Tabs activeTab={mode} onChange={(key) => setMode(key as AuthMode)} style={{ marginTop: 18 }}>
          <Tabs.TabPane key="login" title="登录">
            <div style={{ display: "grid", gap: 14 }}>
              {errorMessage ? <AuthErrorMessage message={errorMessage} /> : null}
              <Input
                aria-label="账号、手机号或邮箱"
                placeholder="账号、手机号或邮箱"
                size="large"
                value={loginValue}
                onChange={setLoginValue}
              />
              <Input.Password
                aria-label="密码"
                placeholder="密码"
                size="large"
                value={loginPassword}
                onChange={setLoginPassword}
              />
              <Button
                type="primary"
                size="large"
                long
                loading={submitting}
                disabled={!loginValue.trim() || loginPassword.length < 6}
                onClick={() => void handleLoginSubmit()}
              >
                登录
              </Button>
              <Button size="large" long disabled={!wechatCode || submitting} onClick={() => void handleWechatLoginClick()}>
                微信扫码登录
              </Button>
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane key="register" title="注册">
            <div style={{ display: "grid", gap: 14 }}>
              {errorMessage ? <AuthErrorMessage message={errorMessage} /> : null}
              <Input
                aria-label="用户名"
                placeholder="用户名"
                size="large"
                value={registerUsername}
                onChange={setRegisterUsername}
              />
              <Input
                aria-label="姓名"
                placeholder="姓名"
                size="large"
                value={registerRealname}
                onChange={setRegisterRealname}
              />
              <Input
                aria-label="手机号"
                placeholder="手机号"
                size="large"
                value={registerPhone}
                onChange={setRegisterPhone}
              />
              <Input
                aria-label="邮箱"
                placeholder="邮箱"
                size="large"
                value={registerEmail}
                onChange={setRegisterEmail}
              />
              <Input.Password
                aria-label="注册密码"
                placeholder="密码"
                size="large"
                value={registerPassword}
                onChange={setRegisterPassword}
              />
              <Button
                type="primary"
                size="large"
                long
                loading={submitting || wechatBinding}
                disabled={
                  !registerUsername.trim() ||
                  !registerRealname.trim() ||
                  !registerPhone.trim() ||
                  registerPassword.length < 6
                }
                onClick={() => void handleRegisterSubmit()}
              >
                注册并登录
              </Button>
            </div>
          </Tabs.TabPane>
        </Tabs>
      </Card>
    </main>
  );
}

/** 页面内认证错误提示。 */
function AuthErrorMessage({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        border: "1px solid #ffccc7",
        borderRadius: 8,
        background: "#fff2f0",
        color: "#b42318",
        padding: "9px 11px",
        fontSize: 13,
        lineHeight: 1.45,
      }}
    >
      {message}
    </div>
  );
}

/** 将认证接口错误映射为页面提示。 */
function resolveLoginErrorMessage(error: unknown): string {
  if (!isHttpClientError(error)) {
    return "登录失败，请检查账号或密码。";
  }

  if (error.message === "account-not-found") {
    return "登录失败，无此账号。";
  }
  if (error.message === "invalid-password") {
    return "登录失败，账号正确，密码错误。";
  }
  if (error.message === "account-disabled") {
    return "登录失败，账号已被停用。";
  }

  return "登录失败，请检查账号或密码。";
}
